import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { contractors as contractorsApi } from '@/api/catalogs';
import {
  materialTransfersForProcessing,
  type MaterialTransferListItem,
} from '@/api/materialTransfersForProcessing';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import ImportedBadge from '@/shared/components/ImportedBadge';
import DocListFilters, { DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { useOrgNames, agOrgCol } from '@/shared/hooks/useOrgNames';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';

const PERMISSION = 'production.material_transfer.manage';

export default function MaterialTransfersForProcessingPage() {
  const navigate = useNavigate();
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const f = useDocListFilters();
  const orgNames = useOrgNames();

  // Серверна пагінація (Норма #29 — InfiniteDocGrid замість client-side limit 500).
  const { datasource, total } = useInfiniteGridDatasource<MaterialTransferListItem>(
    (limit, offset, sort) => materialTransfersForProcessing.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [...f.queryKey],
    { attachmentsType: 'doc.material_transfer_for_processing' },
  );
  const sc = useSignControlInfinite<MaterialTransferListItem>('doc.material_transfer_for_processing', f.orgId);

  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColDef<MaterialTransferListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: MaterialTransferListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: 'Номер', width: 150, sortable: true },
    {
      field: 'document_date', headerName: 'Дата', width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
    },
    agOrgCol(orgNames),
    {
      field: 'processor_contractor_id', headerName: 'Переробник', sortable: true,
      valueFormatter: (p) => (p.value ? cName.get(p.value as string) ?? '' : ''),
    },
    {
      field: 'total_cost', headerName: 'Вартість сировини', width: 170, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null
        ? Number(p.value).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) => p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: 'Проведено', width: 120, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [cName, orgNames]);

  const canCreate = f.orgId ? allowedOrgIds.has(f.orgId) : perms.availableOrgs.length > 0;
  const tooltipNew = !canCreate
      ? `Немає прав створювати Передачу в переробку у цій юрособі (${PERMISSION})`
      : undefined;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Передача сировини в переробку<TitleTotal total={total} /></h2>
        <button
            className="primary"
          style={{ marginLeft: 'auto' }}
            disabled={!canCreate}
            title={tooltipNew}
            onClick={() => navigate('/material-transfers-for-processing/new')}
          >
            + Нова передача
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
      </DocListFiltersRow>
      <InfiniteDocGrid<MaterialTransferListItem>
        deleteRow={{ remove: (id) => materialTransfersForProcessing.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="material-transfers-for-processing"
        onRowDoubleClicked={(r) => navigate(`/material-transfers-for-processing/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark
          ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
